/**
 * TalentLens AI - Interviews Page
 * View and manage all scheduled interviews
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { INTERVIEW_STATUS_LABELS, INTERVIEW_TYPE_LABELS, INTERVIEW_RECOMMENDATION_LABELS } from '@talentlens/shared';

type ViewMode = 'list' | 'calendar';
type InterviewStatus = 'scheduled' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';

export default function InterviewsPage() {
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showFeedbackModal, setShowFeedbackModal] = useState<string | null>(null);

  const { data: interviewsData, isLoading } = useQuery({
    queryKey: ['interviews', statusFilter],
    queryFn: () => api.getInterviews({ status: statusFilter || undefined, limit: '50' }),
  });

  const { data: upcomingData } = useQuery({
    queryKey: ['upcoming-interviews'],
    queryFn: () => api.getUpcomingInterviews(7),
  });

  const interviews = interviewsData?.data?.interviews || [];
  const upcomingInterviews = upcomingData?.data || [];

  const cancelMutation = useMutation({
    mutationFn: (id: string) => api.cancelInterview(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interviews'] });
      queryClient.invalidateQueries({ queryKey: ['upcoming-interviews'] });
    },
  });

  const handleCancel = (id: string) => {
    if (confirm('Are you sure you want to cancel this interview?')) {
      cancelMutation.mutate(id);
    }
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-48" />
        <div className="grid gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Interviews</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage and track all scheduled interviews
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex border dark:border-gray-600 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 text-sm ${
                viewMode === 'list'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
              }`}
            >
              <ListIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-3 py-1.5 text-sm ${
                viewMode === 'calendar'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
              }`}
            >
              <CalendarIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm"
          >
            <option value="">All Statuses</option>
            <option value="scheduled">Scheduled</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Upcoming Interviews Widget */}
      {upcomingInterviews.length > 0 && (
        <div className="bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800 rounded-xl p-4">
          <h3 className="font-medium text-primary-900 dark:text-primary-200 mb-3">
            Upcoming This Week ({upcomingInterviews.length})
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {upcomingInterviews.slice(0, 3).map((interview: any) => (
              <div
                key={interview.id}
                className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm"
              >
                <p className="font-medium text-gray-900 dark:text-white">{interview.title}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{interview.candidateName}</p>
                <div className="flex items-center gap-2 mt-2 text-xs text-gray-500 dark:text-gray-400">
                  <ClockIcon className="w-3 h-3" />
                  <span>{formatDateTime(interview.scheduledAt)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Interview List */}
      {viewMode === 'list' ? (
        <div className="space-y-4">
          {interviews.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl shadow-sm">
              <CalendarIcon className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                No interviews found
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {statusFilter
                  ? `No interviews with status "${statusFilter}"`
                  : 'Schedule interviews from the candidate pipeline'}
              </p>
            </div>
          ) : (
            interviews.map((interview: any) => (
              <InterviewCard
                key={interview.id}
                interview={interview}
                onCancel={() => handleCancel(interview.id)}
                onAddFeedback={() => setShowFeedbackModal(interview.id)}
              />
            ))
          )}
        </div>
      ) : (
        <CalendarView interviews={interviews} />
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <FeedbackModal
          interviewId={showFeedbackModal}
          onClose={() => setShowFeedbackModal(null)}
          onSuccess={() => {
            setShowFeedbackModal(null);
            queryClient.invalidateQueries({ queryKey: ['interviews'] });
          }}
        />
      )}
    </div>
  );
}

function InterviewCard({
  interview,
  onCancel,
  onAddFeedback,
}: {
  interview: any;
  onCancel: () => void;
  onAddFeedback: () => void;
}) {
  const isPast = new Date(interview.scheduledAt) < new Date();
  const canAddFeedback = isPast && !interview.hasFeedback && interview.status !== 'cancelled';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
              {interview.title}
            </h3>
            <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(interview.status)}`}>
              {INTERVIEW_STATUS_LABELS[interview.status as InterviewStatus] || interview.status}
            </span>
            <span className={`px-2 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>
              {INTERVIEW_TYPE_LABELS[interview.type as keyof typeof INTERVIEW_TYPE_LABELS] || interview.type}
            </span>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600 dark:text-gray-400">Candidate</p>
              <p className="font-medium text-gray-900 dark:text-white">{interview.candidateName}</p>
              <p className="text-gray-500 dark:text-gray-400">{interview.candidateEmail}</p>
            </div>

            <div>
              <p className="text-gray-600 dark:text-gray-400">Job</p>
              <p className="font-medium text-gray-900 dark:text-white">{interview.jobTitle}</p>
            </div>

            <div>
              <p className="text-gray-600 dark:text-gray-400">When</p>
              <p className="font-medium text-gray-900 dark:text-white">
                {formatDateTime(interview.scheduledAt)}
              </p>
              <p className="text-gray-500 dark:text-gray-400">{interview.durationMinutes} minutes</p>
            </div>

            {interview.meetingLink && (
              <div>
                <p className="text-gray-600 dark:text-gray-400">Meeting Link</p>
                <a
                  href={interview.meetingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Join Meeting
                </a>
              </div>
            )}

            {interview.location && (
              <div>
                <p className="text-gray-600 dark:text-gray-400">Location</p>
                <p className="font-medium text-gray-900 dark:text-white">{interview.location}</p>
              </div>
            )}
          </div>

          {/* Interviewers */}
          {interview.interviewers?.length > 0 && (
            <div className="mt-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Interviewers</p>
              <div className="flex flex-wrap gap-2">
                {interview.interviewers.map((i: any, idx: number) => (
                  <span
                    key={idx}
                    className={`px-2 py-1 rounded text-xs ${
                      i.response === 'accepted'
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                        : i.response === 'declined'
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {i.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 ml-4">
          {canAddFeedback && (
            <button
              onClick={onAddFeedback}
              className="flex items-center gap-1 px-3 py-1.5 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700"
            >
              <StarIcon className="w-4 h-4" />
              Add Feedback
            </button>
          )}

          {interview.hasFeedback && (
            <span className="px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg text-sm text-center">
              Feedback Added
            </span>
          )}

          {interview.status === 'scheduled' && !isPast && (
            <button
              onClick={onCancel}
              className="flex items-center gap-1 px-3 py-1.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg text-sm hover:bg-red-200 dark:hover:bg-red-900/50"
            >
              <XIcon className="w-4 h-4" />
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CalendarView({ interviews }: { interviews: any[] }) {
  // Group interviews by date
  const interviewsByDate = interviews.reduce((acc, interview) => {
    const date = new Date(interview.scheduledAt).toDateString();
    if (!acc[date]) acc[date] = [];
    acc[date].push(interview);
    return acc;
  }, {} as Record<string, any[]>);

  const sortedDates = Object.keys(interviewsByDate).sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime()
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
      {sortedDates.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No interviews scheduled
        </div>
      ) : (
        <div className="space-y-6">
          {sortedDates.map((date) => (
            <div key={date}>
              <h3 className="font-medium text-gray-900 dark:text-white mb-3">
                {formatDate(date)}
              </h3>
              <div className="space-y-2 border-l-2 border-primary-200 dark:border-primary-700 pl-4">
                {interviewsByDate[date].map((interview: any) => (
                  <div
                    key={interview.id}
                    className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg"
                  >
                    <div className="text-sm font-medium text-primary-600 dark:text-primary-400 w-16">
                      {formatTime(interview.scheduledAt)}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-white">{interview.title}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {interview.candidateName} - {interview.jobTitle}
                      </p>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(interview.status)}`}>
                      {interview.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FeedbackModal({
  interviewId,
  onClose,
  onSuccess,
}: {
  interviewId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [rating, setRating] = useState(3);
  const [recommendation, setRecommendation] = useState<string>('neutral');
  const [strengths, setStrengths] = useState('');
  const [concerns, setConcerns] = useState('');
  const [notes, setNotes] = useState('');

  const submitMutation = useMutation({
    mutationFn: () => api.submitInterviewFeedback(interviewId, {
      rating,
      recommendation,
      strengths,
      concerns,
      notes,
    }),
    onSuccess,
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Interview Feedback</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Rating */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Overall Rating
            </label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  className="p-1"
                >
                  <StarIcon
                    className={`w-8 h-8 ${
                      star <= rating
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-gray-300 dark:text-gray-600'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Recommendation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Recommendation
            </label>
            <div className="grid grid-cols-5 gap-2">
              {(['strong_no', 'no', 'neutral', 'yes', 'strong_yes'] as const).map((rec) => (
                <button
                  key={rec}
                  onClick={() => setRecommendation(rec)}
                  className={`px-2 py-2 rounded text-xs text-center ${
                    recommendation === rec
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {INTERVIEW_RECOMMENDATION_LABELS[rec]}
                </button>
              ))}
            </div>
          </div>

          {/* Strengths */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Strengths
            </label>
            <textarea
              value={strengths}
              onChange={(e) => setStrengths(e.target.value)}
              placeholder="What did the candidate do well?"
              rows={3}
              className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          {/* Concerns */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Concerns
            </label>
            <textarea
              value={concerns}
              onChange={(e) => setConcerns(e.target.value)}
              placeholder="Any concerns or areas for improvement?"
              rows={3}
              className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Additional Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any other observations..."
              rows={2}
              className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={() => submitMutation.mutate()}
            disabled={!strengths || !concerns || submitMutation.isPending}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {submitMutation.isPending ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Utility functions
function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    scheduled: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    confirmed: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    completed: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
    cancelled: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    no_show: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
  };
  return colors[status] || colors.scheduled;
}

function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';

  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Icons
function ListIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
    </svg>
  );
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function StarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
